import { Button, LoadingOverlay } from '@mantine/core';
import { useEffect, useState } from 'react';

import { Declaration, DeclarationHighValueGroupType } from '@e-origin/shared';

import { HideElementFor, LeavePageNotification, StickyContainer } from '@components';
import { useDeclarant } from '@hooks/declarant';
import { useSettings } from '@hooks/useSettings';
import { isHighValueDeclaration } from '@utils';
import { DeclarantAndRepresentativeForm } from './DeclarantAndRepresentativeForm';
import { StakeholderExporterSection } from './StakeholderExporterSection';
import { StakeholderImporterSection } from './StakeholderImporterSection';
import { ExtendedDeclaration, SupplyChainAndCustomsSections } from './SupplyChainAndCustomsSections';
import { useDeclarationStakeholdersForm } from './useDeclarationStakeholdersForm';

interface DeclarationViewStakeholdersProps {
  editDisabled: boolean;
  declaration?: Declaration;
}

export const DeclarationViewStakeholders: React.FC<DeclarationViewStakeholdersProps> = ({ editDisabled, declaration }) => {
  const [declarationGroup, setDeclarationGroup] = useState<string>(declaration?.generalInfo?.group);

  const { form, onSubmit, isSaving, buildFormValues } = useDeclarationStakeholdersForm({ declaration });

  const { data: declarant, isLoading } = useDeclarant();
  const { data: settings } = useSettings();

  const isHighValue = isHighValueDeclaration(declaration?.generalInfo?.group);

  useEffect(() => {
    form.setValues(buildFormValues(declaration));
    form.resetDirty({});
    setDeclarationGroup(declaration?.generalInfo?.group);
  }, [declaration]);

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <form onSubmit={form.onSubmit(onSubmit)} style={{ paddingRight: '30%' }}>
        {declaration && settings && (
          <>
            <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2}>
              <StakeholderExporterSection
                form={form}
                editDisabled={editDisabled}
                declaration={declaration}
                isHighValue={isHighValue}
                declarationGroup={declarationGroup}
              />
            </HideElementFor>

            <StakeholderImporterSection
              form={form}
              editDisabled={editDisabled}
              declaration={declaration}
              isHighValue={isHighValue}
              declarationGroup={declarationGroup}
            />

            <HideElementFor systems={['DECO', 'IDMS_LV']} declarationGroup={declarationGroup}>
              <SupplyChainAndCustomsSections
                form={form}
                editDisabled={editDisabled}
                declaration={declaration as ExtendedDeclaration}
                declarationGroup={declarationGroup}
                isExport={declaration?.generalInfo?.isExport}
              />
            </HideElementFor>

            <DeclarantAndRepresentativeForm form={form} declaration={declaration as ExtendedDeclaration} declarationGroup={declarationGroup} />
          </>
        )}

        <StickyContainer noPl>
          <Button
            data-pw="submit-btn"
            type="submit"
            key="save-modal-btn"
            variant="filled"
            loading={isSaving}
            size="lg"
            my="xl"
            disabled={editDisabled || !form.isValid()}
          >
            Save
          </Button>
        </StickyContainer>
      </form>
    </>
  );
};
